import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useTextViewStore } from '../../shared/store/TextViewStore';
import { useTextListActions } from '../../shared/hooks/useApi/useTextList';
import { AlertPosition, AlertType, TextBlockType } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import { useTextIndexing } from '../../shared/hooks/useTextIndexing';
import { useValidation } from '../../shared/hooks/useValidation';
import { useImage } from '../../shared/hooks/useApi/useImage';
import { UpdateTextBlockReq } from '../../shared/dto/ReqDtoRepository';
import { convertTextBlockToUpdateReq } from '../../shared/converter/converter';
import { toUpdateTextReq } from '../../shared/util/textTag';

interface TextOverviewEditActionsProps {
  showConfirm?: boolean;
  showDelete?: boolean;
  transparent?: boolean;
  keepConfirmMounted?: boolean;
}

const TextOverviewEditActions: React.FC<TextOverviewEditActionsProps> = ({
  showConfirm = true,
  showDelete = true,
  transparent = false,
  keepConfirmMounted = false,
}) => {
  const { isEditMode } = useEditMode();
  const { isIndexingMode } = useTextIndexing();
  const { updateText, deleteText } = useTextListActions();
  const activeText = useTextViewStore((s) => s.activeText);
  const textHasChanged = useTextViewStore((s) => s.hasChanged);
  const activeTextBlockList = useTextViewStore((s) => s.activeTextBlockList);
  const hasMultiSelected = useTextViewStore((s) => s.multiSelectedText.length > 0);
  const { enqueueAlert } = useStandardAlertStore();
  const { isValidTextBlockLinkUrl } = useValidation();
  const { uploadTextBlockImage } = useImage();

  const hasChanged = textHasChanged || activeTextBlockList.some((tb) => tb.hasChanged);

  if (!isEditMode || isIndexingMode || !activeText || hasMultiSelected) return null;

  const handleConfirm = async () => {
    if (activeTextBlockList.some((tb) =>
      tb.type === TextBlockType.LINK && !isValidTextBlockLinkUrl(tb.url || '')
    )) {
      return;
    }

    const changedEntries = activeTextBlockList
      .map((tb, index) => ({ block: tb, index }))
      .filter(({ block }) => block.hasChanged);

    const finalizedTextBlocks: UpdateTextBlockReq[] = [];

    for (const { block, index } of changedEntries) {
      const req = convertTextBlockToUpdateReq(block, index);

      let nextReq = req;
      if (
        block.type === TextBlockType.IMAGE &&
        block.imageChanged
      ) {
        const uploaded = await uploadTextBlockImage(activeText.id, req);
        if (!uploaded) return;
        nextReq = uploaded;
      }

      finalizedTextBlocks.push(nextReq);
    }

    await updateText(
      toUpdateTextReq(activeText, {
        textBlocks: finalizedTextBlocks.length > 0 ? finalizedTextBlocks : undefined,
      }),
    );
  };

  const handleDelete = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_TEXT'),
      callBack: async () => {
        await deleteText({ textId: activeText.id });
      },
    });
  };

  return (
    <Actions
      data-has-changed={hasChanged ? 'true' : 'false'}
    >
      {showConfirm && (hasChanged || keepConfirmMounted) && (
        <UniversalIconBtn
          isPrimary={true}
          size="fit"
          border={!transparent}
          isTransparent={transparent}
          backgroundImg={confirmIcon}
          backgroundImgHover={confirmIcon2}
          onClick={handleConfirm}
        />
      )}
      {showDelete && (
        <UniversalIconBtn
          size="fit"
          border={!transparent}
          isTransparent={transparent}
          backgroundImg={deleteCloseIcon}
          backgroundImgHover={deleteOpenIcon}
          onClick={handleDelete}
        />
      )}
    </Actions>
  );
};

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
  margin-left: auto;
`;

export default TextOverviewEditActions;
